import { Check } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import type { Theme, ThemePreference } from '../../lib/theme';

const OPTIONS: Array<{ value: ThemePreference; label: string; hint: string }> = [
    { value: 'dark', label: 'Midnight', hint: 'Dark workspace. Easy on the eyes in a dim workshop.' },
    { value: 'ivory', label: 'Ivory', hint: 'Warm, light workspace. Crisp in daylight and at a desk.' },
    { value: 'system', label: 'Match this device', hint: "Follows your phone or computer's light and dark setting." },
];

/**
 * Settings → Appearance. The choice is per device, like the device's own
 * light/dark setting — see lib/theme.ts.
 *
 * Each option is a real miniature of the app rendered inside its own
 * data-theme scope, so the preview is built from the same tokens as the
 * screens it stands for and cannot drift from them.
 */
export const AppearanceSection = () => {
    const { preference, setPreference } = useTheme();

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-xl font-bold text-fg">Appearance</h3>
                <p className="text-sm text-fg-2">
                    Choose how ForgeDash looks on this device. The side menu stays Midnight in every theme.
                </p>
            </div>

            <div role="radiogroup" aria-label="Theme" className="grid gap-4 sm:grid-cols-3">
                {OPTIONS.map((opt) => {
                    const selected = preference === opt.value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => setPreference(opt.value)}
                            className={`group relative min-w-0 flex flex-col justify-start text-left rounded-[var(--fd-r-lg)] border p-2 transition-colors ${
                                selected ? 'border-gold bg-gold-fill/10' : 'border-line hover:border-line-glow'
                            }`}
                        >
                            <ThemePreview option={opt.value} />
                            <span className="flex items-start gap-2 px-1.5 pt-3 pb-1">
                                <span
                                    aria-hidden="true"
                                    className={`mt-0.5 grid place-items-center w-5 h-5 shrink-0 rounded-full border ${
                                        selected ? 'bg-gold-fill border-gold-fill text-on-gold' : 'border-line-strong'
                                    }`}
                                >
                                    {selected && <Check className="w-3 h-3" strokeWidth={3} />}
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-sm font-semibold text-fg">{opt.label}</span>
                                    <span className="block text-fd-2xs text-fg-3 leading-snug mt-0.5">{opt.hint}</span>
                                </span>
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

/** A miniature screen: Midnight rail, then the workspace in the given theme. */
const MiniScreen = ({ theme }: { theme: Theme }) => (
    <div data-theme={theme} className="flex h-full bg-canvas">
        <div data-theme="dark" className="w-[22%] bg-sunken flex flex-col gap-1.5 p-1.5">
            <span className="h-1.5 w-3/4 rounded-full bg-gold-fill/80" />
            <span className="h-1 w-full rounded-full bg-tint/15" />
            <span className="h-1 w-full rounded-full bg-tint/15" />
            <span className="h-1 w-2/3 rounded-full bg-tint/15" />
        </div>
        <div className="flex-1 p-2 flex flex-col gap-1.5 min-w-0">
            <span className="h-1.5 w-1/2 rounded-full bg-fg/70" />
            <div className="flex gap-1.5 flex-1">
                <div className="flex-1 rounded-[4px] bg-card border border-line shadow-soft p-1 flex flex-col gap-1">
                    <span className="h-1 w-3/4 rounded-full bg-fg-3/50" />
                    <span className="h-1 w-1/2 rounded-full bg-fg-4/40" />
                    <span className="mt-auto h-1.5 w-1/3 rounded-full bg-success/70" />
                </div>
                <div className="w-[38%] rounded-[4px] bg-card border border-line shadow-soft p-1 flex flex-col gap-1">
                    <span className="h-1 w-1/2 rounded-full bg-fg-3/50" />
                    <span className="h-2 w-3/4 rounded-sm bg-fg/80" />
                </div>
            </div>
        </div>
    </div>
);

const ThemePreview = ({ option }: { option: ThemePreference }) => (
    <span
        aria-hidden="true"
        className="block aspect-[16/10] overflow-hidden rounded-[var(--fd-r-md)] border border-line"
    >
        {option === 'system' ? (
            <span className="relative block h-full">
                <span className="absolute inset-0"><MiniScreen theme="dark" /></span>
                <span className="absolute inset-0 [clip-path:polygon(100%_0,100%_100%,0_100%)]"><MiniScreen theme="ivory" /></span>
            </span>
        ) : (
            <MiniScreen theme={option} />
        )}
    </span>
);
